import React from 'react';
import PageHeader from '../../components/shared/PageHeader';
import StatCard from '../../components/shared/StatCard';
import { Card, StatusPill } from '../../components/shared/DataToolbar';
import { LoadingState, ErrorState } from '../../components/shared/Pending';
import { useApi } from '../../api/useApi';
import { adminDashboard, listUsers, listAdminBookings, listAdminProperties } from '../../api/adminApi';
import { Users2, Home, CalendarDays, DollarSign } from 'lucide-react';
import { Link } from 'react-router-dom';

const rows = (value) => Array.isArray(value) ? value : (value?.items || value?.data || []);
const money = (value) => value == null || value === '' ? '—' : `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export default function AdminDashboard() {
  const { data, loading, error, refetch } = useApi(adminDashboard, []);
  const { data: usersData, loading: usersLoading } = useApi(() => listUsers({ per_page: 5 }), []);
  const { data: bookingsData, loading: bookingsLoading } = useApi(() => listAdminBookings({ per_page: 6 }), []);
  const { data: propertiesData, loading: propertiesLoading } = useApi(() => listAdminProperties({ per_page: 6 }), []);

  if (loading) return <LoadingState label="Loading platform dashboard…" />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;

  const stats = data?.stats || data || {};
  const users = rows(usersData);
  const bookings = rows(bookingsData);
  const properties = rows(propertiesData);

  return (
    <div>
      <PageHeader
        title="Platform dashboard"
        description="Bookings, properties, users and marketplace activity that need attention."
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Total users" value={stats.total_users ?? '—'} icon={Users2} tone="sky" />
        <StatCard label="Properties" value={stats.total_properties ?? '—'} icon={Home} tone="amber" />
        <StatCard label="Bookings" value={stats.total_bookings ?? '—'} icon={CalendarDays} tone="slate" />
        <StatCard label="Gross booking value" value={money(stats.gross_booking_volume)} icon={DollarSign} tone="teal" />
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-[1.35fr_1fr]">
        <Card className="p-6">
          <div className="mb-4 flex items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-slate-900">Recent bookings</h3>
              <p className="mt-0.5 text-xs text-slate-500">Latest reservations across StayEasy.</p>
            </div>
            <Link to="/admin/bookings" className="text-xs font-medium text-teal-700 hover:underline">View all</Link>
          </div>

          {bookingsLoading ? (
            <RowsSkeleton rows={5} />
          ) : bookings.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-500">No bookings yet.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {bookings.slice(0, 6).map((booking) => (
                <Link key={booking.id} to={`/admin/bookings`} className="flex items-center gap-3 py-3 hover:bg-slate-50/70">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-slate-900">{booking.property?.title || booking.property?.name || `Booking #${booking.id}`}</div>
                    <div className="mt-0.5 text-xs text-slate-500">{booking.check_in || '—'} → {booking.check_out || '—'} · {booking.guests || 1} guest{Number(booking.guests || 1) === 1 ? '' : 's'}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-slate-900">{money(booking.total_amount ?? booking.total)}</div>
                    <div className="mt-1"><StatusPill status={booking.status} /></div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-6">
          <div className="mb-4 flex items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-slate-900">Latest users</h3>
              <p className="mt-0.5 text-xs text-slate-500">Recently created admin and host accounts.</p>
            </div>
            <Link to="/admin/admin-users" className="text-xs font-medium text-teal-700 hover:underline">Manage</Link>
          </div>

          {usersLoading ? (
            <RowsSkeleton rows={5} />
          ) : users.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-500">No users.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {users.slice(0, 5).map((user) => (
                <div key={user.id} className="flex items-center gap-3 py-3">
                  {user.avatar ? <img src={user.avatar} className="h-9 w-9 rounded-full object-cover" alt="" /> : <div className="grid h-9 w-9 place-items-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">{(user.name || '?').slice(0, 1).toUpperCase()}</div>}
                  <div className="min-w-0 flex-1"><div className="truncate font-medium text-slate-900">{user.name || 'Unnamed user'}</div><div className="truncate text-xs text-slate-500">{user.email}</div></div>
                  <span className="text-xs capitalize text-slate-500">{user.role || user.type || 'user'}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card className="mt-6 p-6">
        <div className="mb-4 flex items-center justify-between gap-4">
          <div>
            <h3 className="font-semibold text-slate-900">Recently added properties</h3>
            <p className="mt-0.5 text-xs text-slate-500">Quick access to active and newly created listings.</p>
          </div>
          <Link to="/admin/properties" className="text-xs font-medium text-teal-700 hover:underline">View properties</Link>
        </div>

        {propertiesLoading ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 6 }).map((_, index) => <div key={index} className="h-24 animate-pulse rounded-xl bg-slate-100" />)}</div>
        ) : properties.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-500">No properties yet.</div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {properties.slice(0, 6).map((property) => (
              <Link key={property.id} to="/admin/properties" className="rounded-xl border border-slate-200 p-4 transition-colors hover:border-teal-300 hover:bg-teal-50/30">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0"><div className="truncate text-sm font-medium text-slate-900">{property.title || property.name || `Property #${property.id}`}</div><div className="mt-1 truncate text-xs text-slate-500">{property.location || [property.location_details?.city, property.location_details?.state].filter(Boolean).join(', ') || 'Location unavailable'}</div></div>
                  <StatusPill status={property.status} />
                </div>
                <div className="mt-4 flex items-center justify-between text-xs text-slate-500"><span>{property.host?.name || property.admin?.name || 'Host'}</span><span className="font-semibold text-slate-900">{money(property.price_per_night ?? property.price)} / night</span></div>
              </Link>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

const RowsSkeleton = ({ rows = 5 }) => (
  <div className="divide-y divide-slate-100 animate-pulse">
    {Array.from({ length: rows }).map((_, index) => <div key={index} className="flex items-center gap-3 py-3"><div className="h-9 w-9 rounded bg-slate-100" /><div className="flex-1"><div className="h-3 w-1/2 rounded bg-slate-100" /><div className="mt-2 h-2.5 w-1/3 rounded bg-slate-100" /></div><div className="h-5 w-16 rounded bg-slate-100" /></div>)}
  </div>
);
